/* eslint-disable camelcase */
import { useQueryClient } from "@tanstack/react-query"
import { useEffect, useState, useContext } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate, useLocation } from "react-router-dom"
import { Box, Paper, TextField, Tooltip, IconButton, Grid, Typography, Divider } from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, useQuery, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"
import Save from "@mui/icons-material/Save"

// Importación del componente de la grilla de detalle que crearemos luego
import DetalleArticulosInvGrid from "./components/DetalleArticulosInvGrid"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1400px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const ProcesarSolicitudInv = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { state } = useLocation() // Recibimos la fila seleccionada desde la grilla principal
  const { selectedMenuInfo } = useContext(GlobalContext)

  // Estado del formulario que enviará el JSON estructurado
  const [formData, setFormData] = useState({
    solnumero: "",
    sgastatus: "",
    tsolInEg: "",
    sgadescri: "",
    locsolicita: "",
    bodegreso: "",
    bodingreso: "",
    tracodigo: "",
    tratipo: "",
    trasigno: "",
    trametodo: 0,
    detalle: [],
  })

  // Retorno de seguridad si recargan la página directamente
  useEffect(() => {
    if (!state?.solnumero) {
      navigate(-1)
    }
  }, [state, navigate])

  // --- EXTRACCIÓN DEFENSIVA DE LA SOLICITUD ---
  const { data: solData, isLoading: loadSol } = useQuery({
    queryKey: ["getDetalleSolicitudInvEdit", state?.solnumero],
    queryFn: async () => {
      try {
        const payload = { solnumero: state.solnumero }
        const response = await api.post("/IngEgrporSolicitud/getDetalleSolicitudInv", payload)

        let coreData = null
        const r1 = response?.data
        if (r1?.data?.data?.solnumero) coreData = r1.data.data
        else if (r1?.data?.solnumero) coreData = r1.data
        else if (r1?.solnumero) coreData = r1

        return coreData
      } catch (error) {
        console.error("Error al cargar la solicitud:", error)
        return null
      }
    },
    enabled: !!state?.solnumero,
  })

  // Carga de datos al estado
  useEffect(() => {
    if (solData) {
      setFormData({
        solnumero: solData.solnumero || "",
        sgastatus: solData.sgastatus || "",
        tsolInEg: solData.tsolInEg || "",
        sgadescri: solData.sgadescri || "",
        locsolicita: solData.locsolicita || "",
        bodegreso: solData.bodegreso || "",
        bodingreso: solData.bodingreso || "",
        tracodigo: solData.tracodigo || "",
        tratipo: solData.tratipo || "",
        trasigno: solData.trasigno || "",
        trametodo: solData.trametodo || 0,
        // Inicializamos los artículos agregándoles la llave "cant_afectar" e inicializando arreglos
        detalle: (solData.detalle || []).map((item) => ({
          ...item,
          cant_afectar: 0,
          lotes: [],
          series: [],
        })),
      })
    }
  }, [solData])

  // --- MUTACIÓN PARA GRABAR LA TRANSACCIÓN ---
  const { mutateAsync: ProcesarTransaccion, isPending: isSaving } = useMutation({
    queryKey: ["procesarTransaccionInv"],
    fn: async (data) => {
      const response = await api.post("/IngEgrporSolicitud/procesarTransaccionInv", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: async () => {
      // Invalida la caché global para obligar a refrescar la bandeja principal
      await queryClient.invalidateQueries()
      navigate(-1)
    },
  })

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    if (e) e.preventDefault()

    // 1. Validaciones Generales
    if (!formData.solnumero) return showWarning("No se encontró el número de solicitud.")

    // 2. Validación de Detalle a procesar
    const itemsAProcesar = formData.detalle.filter((item) => parseFloat(item.cant_afectar) > 0)

    if (itemsAProcesar.length === 0) {
      return showWarning("Debe ingresar la cantidad a afectar en al menos un artículo para procesar el movimiento.")
    }

    // 3. Validación estricta de Lotes y Series
    for (const item of itemsAProcesar) {
      const cantAfectar = parseFloat(item.cant_afectar)

      // Valido si maneja Lotes
      if (item.artlote !== 0) {
        const totLotes = item.lotes.reduce((acc, curr) => acc + parseFloat(curr.cantidad || 0), 0)
        if (totLotes !== cantAfectar) {
          return showWarning(
            `El artículo ${item.artcodigo} maneja lotes. La suma de las cantidades en los lotes (${totLotes}) debe ser exactamente igual a la cantidad a procesar (${cantAfectar}).`,
          )
        }
      }

      // Valido si maneja Series
      if (item.artserie !== 0) {
        if (item.series.length !== cantAfectar) {
          return showWarning(
            `El artículo ${item.artcodigo} maneja series. Usted indicó que afectará ${cantAfectar} unidades, pero ha registrado ${item.series.length} series. Deben coincidir exactamente.`,
          )
        }
      }
    }

    try {
      await ProcesarTransaccion(formData)
    } catch (error) {
      console.error("Error al procesar la transacción:", error)
    }
  }

  // Acción de la barra superior (Solo GRABAR)
  const grabarAction = selectedMenuInfo?.data?.barraAcciones?.find((action) => action?.acccaption === "GRABAR")

  const toolbarActions = grabarAction
    ? [
        {
          label: grabarAction.acccaption,
          key: grabarAction.acccaption,
          icon: getIconComponent(grabarAction.accnameicono, grabarAction.acctipoico),
        },
      ]
    : [{ label: "Grabar", key: "GRABAR", icon: <Save /> }]

  const isLoadingGlobal = loadSol || isSaving

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        {/* Toolbar superior con el botón de Grabar */}
        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <IconButton
                onClick={handleSubmit}
                disabled={isLoadingGlobal}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, bgcolor: "white" }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>
            Procesar Transacción de {formData.tratipo || "Inventario"}: {state?.solnumero}
          </b>
        </div>

        <CustomBackdrop isLoading={isLoadingGlobal} />

        <Box sx={StyledRoot}>
          <Paper
            elevation={3}
            sx={{ p: 4, borderRadius: 3, background: "white" }}
            component="form"
            onSubmit={handleSubmit}
          >
            <Typography variant="h6" color="primary" gutterBottom>
              Datos de Cabecera (Solo Lectura)
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={3}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  disabled
                  label="Tipo Solicitud"
                  value={formData.tsolInEg}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  disabled
                  label="Transacción (Método)"
                  value={`${formData.tracodigo} - ${formData.tratipo} (${formData.trasigno})`}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  disabled
                  label="Localidad Solicita"
                  value={formData.locsolicita || "N/A"}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  disabled
                  label={formData.tratipo === "INGRESO" ? "Bodega de Ingreso" : "Bodega de Egreso"}
                  value={formData.tratipo === "INGRESO" ? formData.bodingreso || "N/A" : formData.bodegreso || "N/A"}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  disabled
                  multiline
                  label="Observación / Descripción"
                  value={formData.sgadescri}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h6" color="primary" gutterBottom>
              Detalle de Artículos a Procesar
            </Typography>

            {formData.solnumero ? (
              <DetalleArticulosInvGrid
                detalle={formData.detalle}
                setFormData={setFormData}
                tratipo={formData.tratipo}
                bodcodigo={formData.tratipo === "INGRESO" ? formData.bodingreso : formData.bodegreso}
              />
            ) : (
              <Typography variant="body2" color="textSecondary" sx={{ fontStyle: "italic", mt: 2 }}>
                Cargando artículos...
              </Typography>
            )}
          </Paper>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default ProcesarSolicitudInv
